import { useEffect, useMemo, useState } from 'react'
import { Button, Empty, Tag, Typography } from 'antd'
import { LeftOutlined, RightOutlined } from '@ant-design/icons'
import type { KnowledgeScope } from '../../constants/knowledgeScope'
import { KNOWLEDGE_SCOPE_META } from '../../constants/knowledgeScope'
import { dedupDocLabel } from './dedupFormat'
import './dedupMatchViewer.css'

const { Text } = Typography

export type DedupMatchBlock = {
  query_text: string
  source_text: string
  match_chars: number
  query_start?: number
  query_end?: number
  source_start?: number
  source_end?: number
  query_page?: number
  query_page_end?: number
  query_page_label?: string
  source_page?: number
  source_page_end?: number
  source_page_label?: string
  query_page_estimated?: boolean
  source_page_estimated?: boolean
  query_position_label?: string
  source_position_label?: string
}

export type DedupMatchSource = {
  item_id: number
  title: string
  original_name?: string
  scope: string
  folder_name?: string
  duplicate_rate: number
  matched_chars: number
  match_count: number
  matches: DedupMatchBlock[]
}

function scopeTag(scope: string) {
  if (scope === 'upload') return <Tag color="blue">本地上传</Tag>
  const meta = KNOWLEDGE_SCOPE_META[scope as KnowledgeScope]
  const color = scope === 'enterprise' ? 'purple' : scope === 'personal' ? 'green' : 'default'
  return <Tag color={color}>{meta?.title ?? scope}</Tag>
}

export function DedupDocTitle({
  title,
  originalName,
  strong = true,
}: {
  title: string
  originalName?: string
  strong?: boolean
}) {
  const label = dedupDocLabel(title, originalName)
  return (
    <span className="dedup-doc-title">
      <Text strong={strong}>{label}</Text>
    </span>
  )
}

function formatPosition(m: DedupMatchBlock, side: 'query' | 'source') {
  const page =
    side === 'query'
      ? m.query_page_label || (m.query_page ? `第 ${m.query_page} 页` : '')
      : m.source_page_label || (m.source_page ? `第 ${m.source_page} 页` : '')
  const pos =
    side === 'query'
      ? m.query_position_label ||
        (m.query_start != null && m.query_end != null
          ? `字符 ${m.query_start + 1}–${m.query_end}`
          : '')
      : m.source_position_label ||
        (m.source_start != null && m.source_end != null
          ? `字符 ${m.source_start + 1}–${m.source_end}`
          : '')
  const estimated = side === 'query' ? m.query_page_estimated : m.source_page_estimated
  return { page, pos, estimated }
}

function LocationLine({ m, side }: { m: DedupMatchBlock; side: 'query' | 'source' }) {
  const { page, pos, estimated } = formatPosition(m, side)
  return (
    <div className="dedup-match-pane-loc">
      {page ? (
        <span className={`dedup-page-badge${side === 'source' ? ' dedup-page-badge--source' : ''}`}>
          {page}
          {estimated ? <span className="dedup-page-badge-est">约</span> : null}
        </span>
      ) : null}
      {pos ? <span>{pos}</span> : null}
      <span>{m.match_chars} 字重复</span>
    </div>
  )
}

export function DedupMatchViewer({
  queryTitle,
  queryOriginalName,
  queryScope,
  source,
  queryTotalChars,
  variant = 'modal',
}: {
  queryTitle: string
  queryOriginalName?: string
  queryScope: string
  source: DedupMatchSource
  queryTotalChars?: number
  variant?: 'modal' | 'standalone'
}) {
  const matches = source.matches ?? []
  const [activeIndex, setActiveIndex] = useState(0)

  useEffect(() => {
    setActiveIndex(0)
  }, [source.item_id])

  const safeIndex = matches.length ? Math.min(activeIndex, matches.length - 1) : 0
  const current = matches[safeIndex]

  const hasEstimated = useMemo(
    () => matches.some((m) => m.query_page_estimated || m.source_page_estimated),
    [matches],
  )

  if (!current) {
    return (
      <div className="dedup-match-viewer" style={{ padding: 24 }}>
        <Empty description="暂无重复片段" />
      </div>
    )
  }

  return (
    <div className={`dedup-match-viewer${variant === 'modal' ? ' dedup-match-viewer--modal' : ''}`}>
      <div className="dedup-match-toolbar">
        <div>
          <div className="dedup-match-toolbar-title">重复片段对照</div>
          <div className="dedup-match-toolbar-meta">
            <span>
              与来源重复率 <strong>{source.duplicate_rate.toFixed(2)}%</strong>
            </span>
            <span>重复 {source.matched_chars} 字</span>
            <span>共 {matches.length} 处</span>
            {scopeTag(queryScope)}
            {scopeTag(source.scope)}
          </div>
        </div>
        {matches.length > 1 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Button
              size="small"
              icon={<LeftOutlined />}
              disabled={safeIndex <= 0}
              onClick={() => setActiveIndex((i) => Math.max(0, i - 1))}
            >
              上一处
            </Button>
            <Text type="secondary" style={{ minWidth: 72, textAlign: 'center', fontSize: 13 }}>
              {safeIndex + 1} / {matches.length}
            </Text>
            <Button
              size="small"
              icon={<RightOutlined />}
              disabled={safeIndex >= matches.length - 1}
              onClick={() => setActiveIndex((i) => Math.min(matches.length - 1, i + 1))}
            >
              下一处
            </Button>
          </div>
        ) : null}
      </div>

      <div className="dedup-match-body">
        {matches.length > 1 ? (
          <aside className="dedup-match-list">
            <div className="dedup-match-list-head">片段列表</div>
            <div className="dedup-match-list-scroll">
              {matches.map((m, idx) => {
                const q = formatPosition(m, 'query')
                const s = formatPosition(m, 'source')
                return (
                  <button
                    key={idx}
                    type="button"
                    className={`dedup-match-seg${idx === safeIndex ? ' dedup-match-seg--active' : ''}`}
                    onClick={() => setActiveIndex(idx)}
                  >
                    <div className="dedup-match-seg-top">
                      <span className="dedup-match-seg-index">片段 {idx + 1}</span>
                      <span className="dedup-match-seg-loc">
                        {q.page || '—'} → {s.page || '—'}
                      </span>
                    </div>
                    <span className="dedup-match-seg-snippet">{m.query_text}</span>
                  </button>
                )
              })}
            </div>
          </aside>
        ) : null}

        <div className="dedup-match-panes">
          <div className="dedup-match-pane">
            <div className="dedup-match-pane-head">
              <div className="dedup-match-pane-role">待查文档</div>
              <div className="dedup-match-pane-doc">
                <DedupDocTitle title={queryTitle} originalName={queryOriginalName} strong={false} />
              </div>
              <LocationLine m={current} side="query" />
            </div>
            <div className="dedup-match-pane-body">
              <pre className="dedup-match-text">{current.query_text}</pre>
            </div>
          </div>

          <div className="dedup-match-pane">
            <div className="dedup-match-pane-head">
              <div className="dedup-match-pane-role">相似来源</div>
              <div className="dedup-match-pane-doc">
                <DedupDocTitle title={source.title} originalName={source.original_name} strong={false} />
              </div>
              <LocationLine m={current} side="source" />
            </div>
            <div className="dedup-match-pane-body">
              <pre className="dedup-match-text">{current.source_text}</pre>
            </div>
          </div>
        </div>
      </div>

      <div className="dedup-match-footnote">
        重复位置说明：页码来自正文分页标记（PDF 精确）；Word 等无标记时页码按约 680 字/页估算并标「约」。
        字符位置为归一化正文中的序号（去空白统一后），便于在原文中检索定位。
        {source.folder_name && source.folder_name !== '—' ? ` · 来源文件夹：${source.folder_name}` : ''}
        {queryTotalChars && queryTotalChars > 0 && current.query_start != null
          ? ` · 待查全文约 ${queryTotalChars} 字`
          : ''}
        {hasEstimated ? ' · 当前来源含估算页码' : ''}
      </div>
    </div>
  )
}

export function useDedupMatchSource(
  reportResult:
    | {
        query_title: string
        query_scope: string
        query_original_name?: string
        total_chars?: number
        source_breakdown?: DedupMatchSource[]
      }
    | undefined,
  itemId: number | null,
) {
  return useMemo(() => {
    if (!reportResult?.source_breakdown?.length || !itemId) return null
    return reportResult.source_breakdown.find((s) => s.item_id === itemId) ?? null
  }, [reportResult, itemId])
}
